import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import usePermissionGroupSearch from "@saleor/searches/usePermissionGroupSearch";
import useSearchTenantByBrandId from "@saleor/searches/useSearchTenantByBrandId";
import { validateCreateStaffForm } from "@saleor/utils/validators/staff";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import {
  AddressInput,
  LockStatus,
  SmopStatusEnum,
  SmopUserTypeCode
} from "../../types/globalTypes";
import StaffCreatePage, {
  StaffDetailsFormData
} from "../components/StaffCreatePage/StaffCreatePage";
import { UserType } from "../constants";
import {
  TypedStaffMemberAddMutation,
  useStaffAddressCreate,
  useStaffAddressSetHome
} from "../mutations";
import { StaffMemberAdd } from "../types/StaffMemberAdd";
import { staffListUrl } from "../urls";

export const StaffCreate: React.FC = () => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const handleBack = () => navigate(staffListUrl());

  const {
    loadMore: loadMorePermissionGroups,
    search: searchPermissionGroups,
    result: searchPermissionGroupsOpts
  } = usePermissionGroupSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const [assignMallDialogOpen, setAssignMallDialogOpen] = React.useState(false);

  const handleStaffMemberAdd = (data: StaffMemberAdd) => {
    if (!maybe(() => data.staffCreate.errors.length !== 0)) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      navigate(staffListUrl());
      setAssignMallDialogOpen(!assignMallDialogOpen);
    } else if (data.staffCreate.errors.some(e => e.code === "REQUIRED")) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Some Fields have null value!"
        })
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({ defaultMessage: "Invalid Form!" })
      });
    }
  };

  const [isRunnerRider, setIsRunnerRider] = React.useState(false);
  const [selectedPermissionGroup, setSelectedPermissionGroup] = useState("");
  const [hasManageCouponsPermission, setHasManageCouponsPermission] = useState(
    false
  );

  const [showBrands, setShowBrands] = useState(false);
  const [showMalls, setShowMalls] = useState(false);
  const [showTenants, setShowTenants] = useState(false);

  // Search Query
  const {
    loadMore: loadMoreMall,
    search: searchMalls,
    result: searchMallsOpts
  } = useMallSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const brandsVariable = {
    after: null,
    first: 20,
    mallId: showTenants ? "TWFsbDow" : null,
    query: " ",
    status: SmopStatusEnum.ACT
  };

  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: searchBrandsOpts
  } = useBrandSearch({
    variables: brandsVariable
  });

  const [brandId, setBrandId] = React.useState("");
  const DEFAULT_TENANT_INITIAL_DATA = {
    after: null,
    brand: brandId ? [brandId] : [],
    first: 20,
    query: " ",
    status: SmopStatusEnum.ACT
  };
  const {
    loadMore: loadMoreTenant,
    search: searchTenants,
    result: searchTenantsOpts
  } = useSearchTenantByBrandId({
    variables: DEFAULT_TENANT_INITIAL_DATA
  });

  const malls = maybe(() => searchMallsOpts?.data?.search?.edges, []).map(
    edge => edge.node
  );
  const brands = maybe(() => searchBrandsOpts?.data?.search?.edges, []).map(
    edge => edge.node
  );
  const tenants = maybe(() => searchTenantsOpts?.data?.search?.edges, []).map(
    edge => edge.node
  );

  const availablePermissionGroups = searchPermissionGroupsOpts.data?.search.edges.map(
    edge => edge.node
  );

  useEffect(() => {
    setIsRunnerRider(
      selectedPermissionGroup === "Runner" ||
        selectedPermissionGroup === "Rider"
    );
    const permissionGroup = selectedPermissionGroup?.toUpperCase();
    if ([UserType.STR_OMI_ADMIN].includes(permissionGroup)) {
      // Creating a OMI Admin

      // Hide - Malls & Tenants (Store) Dropdowns
      setShowBrands(false);
      setShowMalls(false);
      setShowTenants(false);
    } else if ([UserType.STR_SELLER_MASTER].includes(permissionGroup)) {
      // Creating a Seller Master
      // Show - Brands dropdown
      // Hide - Malls & Tenants (Store) Dropdowns
      setShowBrands(true);
      setShowMalls(false);
      setShowTenants(false);
    } else if (
      [
        UserType.STR_MALL_ADMIN,
        UserType.STR_OMI_OPS,
        UserType.STR_RIDER,
        UserType.STR_RUNNER
      ].includes(permissionGroup)
    ) {
      // Creating a Mall Admin
      // Show - Malls Dropdown
      // Hide - Brands & Tenants (Store) Dropdowns
      setShowMalls(true);
      setShowBrands(false);
      setShowTenants(false);
    } else if ([UserType.STR_STORE_ADMIN].includes(permissionGroup)) {
      // Creating a Store Admin
      // Show - Brands & Tenants (Store) Dropdowns
      // Hide - Malls Dropdown
      setShowMalls(false);
      setShowBrands(true);
      setShowTenants(true);
    } else {
      // Hide all dropdowns
      setShowMalls(false);
      setShowBrands(false);
      setShowTenants(false);
    }
  }, [selectedPermissionGroup]);

  useEffect(() => {
    const selectedGroup = availablePermissionGroups?.find(
      group => group.name === selectedPermissionGroup
    );
    const hasManageCoupons = selectedGroup?.permissions.some(
      permission => permission.code === "MANAGE_COUPONS"
    );
    setHasManageCouponsPermission(hasManageCoupons);
  }, [selectedPermissionGroup, availablePermissionGroups]);

  const handleUserTypeChange = selectedValue => {
    setSelectedPermissionGroup(selectedValue);
  };

  const fetchTenantsByBrandId = id => {
    setBrandId(id);
  };

  const [formErrors, setFormErrors] = useState([]);

  const [saveAddressCreate] = useStaffAddressCreate({});
  const [saveAddressSetHome] = useStaffAddressSetHome({});

  return (
    <TypedStaffMemberAddMutation onCompleted={handleStaffMemberAdd}>
      {(addStaffMember, addStaffMemberResult) => {
        const handleSubmit = async (formData: StaffDetailsFormData) => {
          const {
            brandId,
            contactNo,
            firstName,
            lastName,
            email,
            mallId,
            password,
            permissionGroup, // The ID of the newly selected permission group
            tenantId
          } = formData;

          const validate = validateCreateStaffForm({
            brandId,
            contactNo,
            email,
            firstName,
            lastName,
            mallId,
            password,
            permissionGroup,
            selectedPermissionGroup,
            tenantId
          });

          if (!validate.isValid) {
            setFormErrors(validate.errors);
            return validate.errors;
          }

          let inputData: Record<string, unknown> = {
            addGroups: formData.permissionGroup
              ? [formData.permissionGroup]
              : [],

            contactNo: formData.contactNo,

            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            malls: formData.malls,
            objectId: "",
            password: formData.password,
            smopUsertypeCode: null,
            status: formData.isActive ? LockStatus.ACTIVE : LockStatus.INACTIVE
          };
          if (selectedPermissionGroup) {
            switch (selectedPermissionGroup.toUpperCase()) {
              case UserType.STR_OMI_ADMIN:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.OMI_ADMIN
                };
                break;
              case UserType.STR_SELLER_MASTER:
                inputData = {
                  ...inputData,
                  objectId: formData.brandId,
                  smopUsertypeCode: SmopUserTypeCode.SELLER_MASTER
                };
                break;
              case UserType.STR_MALL_ADMIN:
                inputData = {
                  ...inputData,
                  objectId: formData.mallId,
                  smopUsertypeCode: SmopUserTypeCode.MALL_ADMIN
                };
                break;
              case UserType.STR_MARKETING:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.MARKETING
                };
                break;
              case UserType.STR_STORE_ADMIN:
                inputData = {
                  ...inputData,
                  objectId: formData.tenantId,
                  smopUsertypeCode: SmopUserTypeCode.STORE_ADMIN
                };
                break;
              case UserType.STR_OMI_OPS:
                inputData = {
                  ...inputData,
                  objectId: formData.mallId,
                  smopUsertypeCode: SmopUserTypeCode.OMI_OPS
                };
                break;
              case UserType.STR_RIDER:
                inputData = {
                  ...inputData,
                  objectId: formData.mallId,
                  smopUsertypeCode: SmopUserTypeCode.RIDER
                };
                break;
              case UserType.STR_RUNNER:
                inputData = {
                  ...inputData,
                  objectId: formData.mallId,
                  smopUsertypeCode: SmopUserTypeCode.RUNNER
                };
                break;
              case UserType.STR_ACQUISITION:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.ACQUISITION
                };
                break;
              case UserType.STR_OMI_FINANCE:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.OMI_FINANCE
                };
                break;
              case UserType.STR_ACCOUNT_MANAGER:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.ACCOUNT_MANAGER
                };
                break;
              case UserType.STR_KAMS:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.ACCOUNT_MANAGER
                };
                break;
              case UserType.STR_FINANCE_SPECIALIST:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.FINANCE_SPECIALIST
                };
                break;
              case UserType.STR_FINANCE_MANAGER:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.FINANCE_MANAGER
                };
                break;
              case UserType.STR_FINANCE_TREASURY_1:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.FINANCE_TREASURY_1
                };
                break;
              case UserType.STR_FINANCE_TREASURY_2:
                inputData = {
                  ...inputData,
                  objectId: null,
                  smopUsertypeCode: SmopUserTypeCode.FINANCE_TREASURY_2
                };
                break;
              default:
                // We only assign values to "objectId" & "smopUsertypeCode" for User Types (1)OMI Admin, (2)Seller Master, (3)Mall Admin & (4)Store Admin
                // We delete "objectId" & "smopUsertypeCode" properties below as a requirement from the backend
                delete inputData.objectId;
                delete inputData.smopUsertypeCode;
                break;
            }
          }

          if (hasManageCouponsPermission) {
            inputData = {
              ...inputData,
              promoCreator: formData.promoCreator
            };
          }

          const result = await addStaffMember({
            variables: {
              first: 100,
              input: inputData
            }
          });

          if (result?.data?.staffCreate?.errors.length > 0) {
            setFormErrors(result.data.staffCreate.errors);
          }

          if (isRunnerRider && result?.data?.staffCreate?.user?.id) {
            const resultUserId = result?.data?.staffCreate?.user.id;

            const addressInputData: AddressInput = {
              city: formData.city,
              companyName: formData.companyName,
              country: formData.country,
              mapPlaceId: formData.mapPlaceId,
              // phone: formData.phone,
              postalCode: formData.postalCode,
              provinceCode: formData.provinceCode,
              provinceName: formData.provinceName,
              streetBrgy: formData.streetBrgy
            };
            const addressCreateData = await saveAddressCreate({
              variables: {
                input: addressInputData,
                userId: resultUserId
              }
            });

            if (addressCreateData?.data?.addressCreate?.address?.id) {
              await saveAddressSetHome({
                variables: {
                  addressId: addressCreateData.data.addressCreate.address.id,
                  userId: resultUserId
                }
              });
            }
          }

          return result.data.staffCreate.errors;
        };

        return (
          <>
            <WindowTitle
              title={intl.formatMessage({
                defaultMessage: "Create User",
                description: "input label"
              })}
            />
            <StaffCreatePage
              mallChoices={malls}
              brandChoices={brands}
              tenantChoices={tenants}
              fetchMalls={query => searchMalls(query)}
              fetchBrands={query => searchBrands(query)}
              fetchTenants={query => searchTenants(query)}
              handleUserTypeChange={handleUserTypeChange}
              fetchTenantsByBrandId={fetchTenantsByBrandId}
              showBrands={showBrands}
              showMalls={showMalls}
              showTenants={showTenants}
              errors={
                formErrors || addStaffMemberResult?.data?.staffCreate?.errors
              }
              disabled={addStaffMemberResult.loading}
              onBack={handleBack}
              initialSearch=""
              onSubmit={data => handleSubmit(data)}
              availablePermissionGroups={searchPermissionGroupsOpts.data?.search.edges.map(
                edge => edge.node
              )}
              saveButtonBarState={addStaffMemberResult.status}
              fetchMorePermissionGroups={{
                hasMore:
                  searchPermissionGroupsOpts.data?.search.pageInfo.hasNextPage,
                loading: searchPermissionGroupsOpts.loading,
                onFetchMore: loadMorePermissionGroups
              }}
              onSearchChange={searchPermissionGroups}
              fetchMoreMalls={{
                hasMore: maybe(
                  () => searchMallsOpts.data.search.pageInfo.hasNextPage
                ),
                loading: searchMallsOpts.loading,
                onFetchMore: loadMoreMall
              }}
              fetchMoreBrands={{
                hasMore: maybe(
                  () => searchBrandsOpts.data.search.pageInfo.hasNextPage
                ),
                loading: searchBrandsOpts.loading,
                onFetchMore: loadMoreBrand
              }}
              fetchMoreTenants={{
                hasMore: maybe(
                  () => searchTenantsOpts.data.search.pageInfo.hasNextPage
                ),
                loading: searchTenantsOpts.loading,
                onFetchMore: loadMoreTenant
              }}
              isRunnerRider={isRunnerRider}
              hasManageCouponsPermission={hasManageCouponsPermission}
              selectedPermissionGroup={selectedPermissionGroup}
              params={undefined}
            />
          </>
        );
      }}
    </TypedStaffMemberAddMutation>
  );
};

export default StaffCreate;
